<template>
  <div>
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <button
          class="tw-bg-white tw-border-solid tw-border-primary-600 tw-text-primary-600 hover:tw-bg-primary-600 hover:tw-text-white tw-px-2 tw-py-1 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
          type="button"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <DotsHorizontalIcon class="tw-w-4 tw-h-4" />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        class="tw-w-56 !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
        side="left"
      >
        <DropdownMenuItem
          v-for="action in actions"
          :key="action.label"
          class="tw-cursor-pointer hover:!tw-bg-gray-100 tw-transition-colors tw-group"
          :class="{ 'tw-opacity-50 tw-cursor-not-allowed': action.disabled }"
          @click="action.disabled ? null : action.onClick()"
          v-tooltip="action.tooltip"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <component
              :is="action.icon"
              class="tw-w-4 tw-h-4"
              :class="action.iconClass"
              v-if="action.icon"
            />
            <span>{{ action.label }}</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      v-model="showConfirmationModal"
      :title="t('common.titles.attention').toUpperCase()"
      :message="confirmationMessage"
      type="danger"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="performDelete"
      @cancel="showConfirmationModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ref, computed, markRaw } from 'vue';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import router from '@/router';
import { useProjectStore } from '@/stores/project.store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import { useCustomerStore } from '@/stores/customer.store';
import DotsHorizontalIcon from '@/assets/icons/DotsHorizontal.svg';
import EditIcon from '@/assets/icons/Edit.svg';
import TrashIcon from '@/assets/icons/Trash.svg';

const { handleApiError } = useApiUtils();
const { deleteProject } = useProjectStore();

const customerStore = useCustomerStore();
const disableActions = computed(() => customerStore.shouldDisableCustomerActions());

const { t } = useI18n();

const props = defineProps<{
  project: any;
}>();

const emit = defineEmits<{
  reloadTable;
}>();

// Modal state
const showConfirmationModal = ref(false);
const confirmationMessage = `${t('customer_management.actions.delete_confirmation_title')} ${
  props.project.name
}`;

// Delete Project
const handleDelete = () => {
  showConfirmationModal.value = true;
};

const performDelete = async () => {
  await handleApiError(
    () => deleteProject([props.project.id]),
    t('customer_management.actions.project_deleted_successfully')
  );
  emit('reloadTable');
};

// Edit Project
const handleEdit = () => {
  router.push({ name: 'edit-project', params: { id: props.project.id } });
};

const actions = ref<any[]>([
  {
    label: t('common.titles.edit'),
    icon: EditIcon,
    iconClass: 'tw-text-blue-500',
    onClick: handleEdit,
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('common.forms.delete'),
    icon: TrashIcon,
    iconClass: 'tw-text-red-500',
    onClick: handleDelete,
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
]);
</script>
